import React, { useMemo, useState } from 'react';
import TDTGraph from './TDTGraph';
import SISIGraph from './SISIGraph';
import ABLBGraph from './ABLBGraph';
import {
  TDT_FREQS, SISI_FREQS, ABLB_INTENSITIES,
  ENV_OPTIONS, TRANSDUCER_OPTIONS, EAR_OPTIONS,
  normaliseSpecial, setTDT, setSISI, setABLB,
  interpretTDT, interpretSISI, interpretABLB, overallConclusion,
} from './specialLogic';

const badgeCls = (tone) =>
  tone === 'neg' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
  : tone === 'pos' ? 'bg-red-100 text-red-800 border-red-300'
  : tone === 'mid' ? 'bg-amber-100 text-amber-800 border-amber-300'
  : 'bg-slate-100 text-slate-600 border-slate-300';

const iconFor = (tone) => tone === 'neg' ? '✓' : tone === 'pos' ? '⚠' : tone === 'mid' ? '~' : '—';

/**
 * SpecialDataEntry — three-section editor (TDT · SISI · ABLB).
 *
 * • Common fields above the sections
 * • Three side-by-side cards, each with editable table + interactive
 *   graph (click on graph writes to table, table edits redraw graph)
 * • Auto interpretation badge per section
 * • Overall Conclusion + Recommendations below
 */
const SpecialDataEntry = ({ data, onChange, audiologistName }) => {
  const state = useMemo(() => normaliseSpecial(data), [data]);
  const [tdtFreq, setTdtFreq] = useState(1000);
  const [sisiEar, setSisiEar] = useState('right');
  const [ablbEar, setAblbEar] = useState('right');

  const setField = (k, v) => onChange({ ...state, [k]: v });
  const patch = (next) => onChange(next);

  const tRes = interpretTDT(state);
  const sRes = interpretSISI(state);
  const aRes = interpretABLB(state);
  const overall = overallConclusion(tRes, sRes, aRes);

  // Auto-fill audiologist name once on mount if empty
  React.useEffect(() => {
    if (!state.clinician && audiologistName) setField('clinician', audiologistName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cellInput = (val, onChangeVal, testId, placeholder = '—') => (
    <input
      type="number"
      step="0.1"
      value={val === undefined || val === null ? '' : val}
      onChange={(e) => onChangeVal(e.target.value)}
      className="w-full px-1 py-0.5 text-center bg-transparent focus:outline-none focus:bg-yellow-50"
      data-testid={testId}
      placeholder={placeholder}
    />
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-auto p-3" data-testid="special-data-entry">
      {/* Common fields */}
      <div className="grid grid-cols-4 gap-2 mb-3 bg-white border border-slate-200 rounded-md p-2" data-testid="special-common-fields">
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Audiometer</span>
          <input type="text" value={state.audiometer} onChange={(e) => setField('audiometer', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="e.g. Interacoustics AC40" data-testid="special-audiometer" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Calibration</span>
          <input type="text" value={state.calibration} onChange={(e) => setField('calibration', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="e.g. Valid (14 Sep 2026)" data-testid="special-calibration" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Test Environment</span>
          <select value={state.environment} onChange={(e) => setField('environment', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:border-indigo-500"
            data-testid="special-environment">
            {ENV_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Transducer</span>
          <select value={state.transducer} onChange={(e) => setField('transducer', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:border-indigo-500"
            data-testid="special-transducer">
            {TRANSDUCER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Test Ear</span>
          <select value={state.test_ear} onChange={(e) => setField('test_ear', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:outline-none focus:border-indigo-500"
            data-testid="special-test-ear">
            {EAR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Clinician</span>
          <input type="text" value={state.clinician} onChange={(e) => setField('clinician', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="Auto-filled from login" data-testid="special-clinician" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Refer By</span>
          <input type="text" value={state.refer_by} onChange={(e) => setField('refer_by', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            placeholder="Self / ENT / Physician" data-testid="special-refer-by" />
        </label>
        <label className="text-xs">
          <span className="block text-slate-600 font-semibold mb-0.5">Purpose</span>
          <input type="text" value={state.purpose} onChange={(e) => setField('purpose', e.target.value)}
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-indigo-500"
            data-testid="special-purpose" />
        </label>
      </div>

      {/* Three-column layout */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        {/* ── TDT ── */}
        <div className="border-2 border-slate-200 rounded-md bg-white p-2" data-testid="special-tdt-card">
          <h3 className="text-sm font-bold text-sky-700 uppercase">Tone Decay Test (TDT)</h3>
          <div className="text-[10px] text-slate-500">Assessment of auditory fatigue</div>
          <textarea
            value={state.tdt.procedure}
            onChange={(e) => patch({ ...state, tdt: { ...state.tdt, procedure: e.target.value } })}
            rows={2}
            className="w-full mt-1 text-[10px] text-slate-600 italic border border-slate-200 rounded px-1 py-0.5 resize-y focus:outline-none focus:border-indigo-500"
            data-testid="tdt-procedure"
          />
          <div className="mt-1">
            <TDTGraph
              values={state.tdt.values}
              selectedFreq={tdtFreq}
              onSelectFreq={setTdtFreq}
              onSetValue={(f, sec) => patch(setTDT(state, f, sec))}
            />
          </div>
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="tdt-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5">Frequency (Hz)</th>
                <th className="border border-slate-300 px-1 py-0.5">Tone Decay Time (seconds)</th>
              </tr>
            </thead>
            <tbody>
              {TDT_FREQS.map((f, idx) => (
                <tr key={f} className={idx % 2 ? 'bg-slate-50' : ''}>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{f.toLocaleString()}</td>
                  <td className="border border-slate-300 p-0">
                    {cellInput(state.tdt.values?.[String(f)], (v) => patch(setTDT(state, f, v === '' ? null : Number(v))), `tdt-cell-${f}`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`mt-2 border rounded p-1.5 ${badgeCls(tRes.tone)}`} data-testid="tdt-interpretation">
            <div className="text-[10px] font-bold uppercase text-slate-600 mb-0.5">Interpretation</div>
            <div className="flex items-start gap-1.5">
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-bold ${tRes.tone === 'neg' ? 'bg-emerald-600' : tRes.tone === 'pos' ? 'bg-red-600' : tRes.tone === 'mid' ? 'bg-amber-500' : 'bg-slate-400'}`}>{iconFor(tRes.tone)}</span>
              <div>
                <div className="text-[11px] font-bold">{tRes.label}</div>
                <div className="text-[10px]">{tRes.note}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── SISI ── */}
        <div className="border-2 border-slate-200 rounded-md bg-white p-2" data-testid="special-sisi-card">
          <h3 className="text-sm font-bold text-sky-700 uppercase">Short Increment Sensitivity Index (SISI)</h3>
          <div className="text-[10px] text-slate-500">Assessment of small intensity change detection</div>
          <textarea
            value={state.sisi.procedure}
            onChange={(e) => patch({ ...state, sisi: { ...state.sisi, procedure: e.target.value } })}
            rows={2}
            className="w-full mt-1 text-[10px] text-slate-600 italic border border-slate-200 rounded px-1 py-0.5 resize-y focus:outline-none focus:border-indigo-500"
            data-testid="sisi-procedure"
          />
          <div className="mt-1">
            <SISIGraph
              right={state.sisi.right}
              left={state.sisi.left}
              plotEar={sisiEar}
              onSetPlotEar={setSisiEar}
              onSetValue={(ear, f, pct) => patch(setSISI(state, ear, f, pct))}
            />
          </div>
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="sisi-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5">Frequency (Hz)</th>
                <th className="border border-slate-300 px-1 py-0.5 text-red-700">RE Score (%)</th>
                <th className="border border-slate-300 px-1 py-0.5 text-blue-700">LE Score (%)</th>
              </tr>
            </thead>
            <tbody>
              {SISI_FREQS.map((f, idx) => (
                <tr key={f} className={idx % 2 ? 'bg-slate-50' : ''}>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{f.toLocaleString()}</td>
                  <td className="border border-slate-300 p-0">
                    {cellInput(state.sisi.right?.[String(f)], (v) => patch(setSISI(state, 'right', f, v === '' ? null : Number(v))), `sisi-cell-right-${f}`)}
                  </td>
                  <td className="border border-slate-300 p-0">
                    {cellInput(state.sisi.left?.[String(f)], (v) => patch(setSISI(state, 'left', f, v === '' ? null : Number(v))), `sisi-cell-left-${f}`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`mt-2 border rounded p-1.5 ${badgeCls(sRes.tone)}`} data-testid="sisi-interpretation">
            <div className="text-[10px] font-bold uppercase text-slate-600 mb-0.5">Interpretation</div>
            <div className="flex items-start gap-1.5">
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-bold ${sRes.tone === 'neg' ? 'bg-emerald-600' : sRes.tone === 'pos' ? 'bg-red-600' : sRes.tone === 'mid' ? 'bg-amber-500' : 'bg-slate-400'}`}>{iconFor(sRes.tone)}</span>
              <div>
                <div className="text-[11px] font-bold">{sRes.label}</div>
                <div className="text-[10px]">{sRes.note}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── ABLB ── */}
        <div className="border-2 border-slate-200 rounded-md bg-white p-2" data-testid="special-ablb-card">
          <h3 className="text-sm font-bold text-sky-700 uppercase">Alternate Binaural Loudness Balance (ABLB)</h3>
          <div className="text-[10px] text-slate-500">Assessment of recruitment · binaural test</div>
          <textarea
            value={state.ablb.procedure}
            onChange={(e) => patch({ ...state, ablb: { ...state.ablb, procedure: e.target.value } })}
            rows={2}
            className="w-full mt-1 text-[10px] text-slate-600 italic border border-slate-200 rounded px-1 py-0.5 resize-y focus:outline-none focus:border-indigo-500"
            data-testid="ablb-procedure"
          />
          <div className="mt-1">
            <ABLBGraph
              right={state.ablb.right}
              left={state.ablb.left}
              plotEar={ablbEar}
              onSetPlotEar={setAblbEar}
              onSetValue={(ear, i, m) => patch(setABLB(state, ear, i, m))}
            />
          </div>
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="ablb-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5">Test Ear Intensity (dB HL)</th>
                <th className="border border-slate-300 px-1 py-0.5 text-red-700">RE Match</th>
                <th className="border border-slate-300 px-1 py-0.5 text-blue-700">LE Match</th>
                <th className="border border-slate-300 px-1 py-0.5">Δ (dB)</th>
              </tr>
            </thead>
            <tbody>
              {ABLB_INTENSITIES.map((i, idx) => {
                const rv = state.ablb.right?.[String(i)];
                const lv = state.ablb.left?.[String(i)];
                const diffR = rv !== undefined && rv !== null && rv !== '' && Number.isFinite(Number(rv)) ? Number(rv) - i : null;
                const diffL = lv !== undefined && lv !== null && lv !== '' && Number.isFinite(Number(lv)) ? Number(lv) - i : null;
                const diffTxt = diffR !== null && diffL !== null ? `${diffR} / ${diffL}` : (diffR !== null ? `${diffR}` : (diffL !== null ? `${diffL}` : '—'));
                return (
                  <tr key={i} className={idx % 2 ? 'bg-slate-50' : ''}>
                    <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{i}</td>
                    <td className="border border-slate-300 p-0">
                      {cellInput(rv, (v) => patch(setABLB(state, 'right', i, v === '' ? null : Number(v))), `ablb-cell-right-${i}`)}
                    </td>
                    <td className="border border-slate-300 p-0">
                      {cellInput(lv, (v) => patch(setABLB(state, 'left', i, v === '' ? null : Number(v))), `ablb-cell-left-${i}`)}
                    </td>
                    <td className="border border-slate-300 px-1 py-0.5 text-center font-mono" data-testid={`ablb-diff-${i}`}>{diffTxt}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={`mt-2 border rounded p-1.5 ${badgeCls(aRes.tone)}`} data-testid="ablb-interpretation">
            <div className="text-[10px] font-bold uppercase text-slate-600 mb-0.5">Interpretation</div>
            <div className="flex items-start gap-1.5">
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-bold ${aRes.tone === 'neg' ? 'bg-emerald-600' : aRes.tone === 'pos' ? 'bg-red-600' : aRes.tone === 'mid' ? 'bg-amber-500' : 'bg-slate-400'}`}>{iconFor(aRes.tone)}</span>
              <div>
                <div className="text-[11px] font-bold">{aRes.label}</div>
                <div className="text-[10px]">{aRes.note}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overall + recommendations */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className={`border rounded p-2 ${badgeCls(overall.tone)}`} data-testid="special-overall-conclusion">
          <div className="text-[10px] font-bold uppercase text-slate-600 mb-1">Overall Conclusion</div>
          <div className="flex items-start gap-2 mb-1">
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[11px] font-bold ${overall.tone === 'neg' ? 'bg-emerald-600' : overall.tone === 'pos' ? 'bg-red-600' : overall.tone === 'mid' ? 'bg-amber-500' : 'bg-slate-400'}`}>{iconFor(overall.tone)}</span>
            <div className="text-[12px] font-bold">{overall.label}</div>
          </div>
          <textarea
            value={state.overall_conclusion || overall.note}
            onChange={(e) => setField('overall_conclusion', e.target.value)}
            rows={3}
            className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 resize-y focus:outline-none focus:border-indigo-500 bg-white"
            data-testid="special-overall-text"
          />
        </div>
        <div className="border border-slate-200 rounded p-2 bg-white" data-testid="special-recommendations">
          <div className="text-[10px] font-bold uppercase text-slate-600 mb-1">Recommendations</div>
          <textarea
            value={state.recommendations}
            onChange={(e) => setField('recommendations', e.target.value)}
            rows={5}
            placeholder={"• Continue routine audiological monitoring as advised.\n• If symptoms persist, consider additional tests (speech in noise, imaging) based on clinical suspicion.\n• Correlate with full audiological evaluation."}
            className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 resize-y focus:outline-none focus:border-indigo-500"
            data-testid="special-recommendations-text"
          />
        </div>
      </div>
    </div>
  );
};

export default SpecialDataEntry;
